import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { API_ENDPOINTS } from '@core/constants/api.constants'
import {
    ELocationQueryParam,
    ILocationItem,
    ILocationResponseData,
    IDistrictResponseData,
    IDistrictItem,
    ELocationType,
    ILocationByTypeItem,
    ILocationByTypeResponse,
    ILocationAncestryResponse,
} from '../models/location.model'

@Injectable({ providedIn: 'root' })
export class LocationService {
    private readonly http = inject(HttpClient)
    private readonly baseUrl = environment.apiBaseUrl

    getLocationsByPostalCode(postalCode: string): Observable<ILocationItem | null> {
        const url = `${this.baseUrl}${API_ENDPOINTS.USER_MANAGEMENT_LOCATIONS}`
        const params = new HttpParams().set(ELocationQueryParam.POSTAL_CODES, postalCode)
        return this.http
            .get<ILocationResponseData>(url, { params })
            .pipe(map((resp) => resp?.data?.[0] ?? null))
    }

    getDistrictsByState(stateName: string): Observable<string[]> {
        const url = `${this.baseUrl}${API_ENDPOINTS.USER_MANAGEMENT_LOCATIONS}`
        const params = new HttpParams().set(ELocationQueryParam.STATE_NAME, stateName)
        return this.http.get<IDistrictResponseData>(url, { params }).pipe(
            map((resp) => {
                const districtNames = resp?.data
                    ?.map((item) => item.district_name)
                    .filter((name): name is string => !!name)
                return districtNames ? [...new Set(districtNames)].sort() : []
            }),
        )
    }

    /**
     * Get locations by type (country, state, district, city, ward)
     * @param type The location type to fetch
     * @param q Optional search query
     * @param limit Optional limit (defaults to 100)
     * @param page Optional page number
     */
    getLocationsByType(
        type: ELocationType,
        q?: string,
        limit = 100,
        page = 1,
    ): Observable<ILocationByTypeItem[]> {
        const url = `${this.baseUrl}${API_ENDPOINTS.USER_MANAGEMENT_LOCATIONS}/type`
        let params = new HttpParams()
            .set('type', type)
            .set('limit', limit.toString())
            .set('page', page.toString())

        if (q && q.trim()) {
            params = params.set('q', q.trim())
        }

        return this.http.get<ILocationByTypeResponse>(url, { params }).pipe(
            map((resp) => {
                return resp?.data || []
            }),
        )
    }

    /**
     * Get location ancestry path by location ID
     * @param locationId The location ID
     */
    getLocationAncestry(locationId: number): Observable<ILocationAncestryResponse> {
        const url = `${this.baseUrl}${API_ENDPOINTS.USER_MANAGEMENT_LOCATIONS}/${locationId}/ancestry`
        return this.http.get<ILocationAncestryResponse>(url)
    }
}
